/**
 * Initiatives API functions
 * @module lib/api/initiatives
 */

import { apiClient } from '@/lib/api/client';
import type { ApiEnvelope } from '@/types/api';
import type {
  InitiativeStatistics,
  Initiative,
  InitiativeQueryParams,
  InitiativeTemplate,
  CityLookup,
  CommunityLookup,
  CreateInitiativeInput,
  CreateInitiativeRewardsInput,
  CreateInitiativePayload,
  FundInitiativeInput,
  InviteParticipantInput,
  ReactivateInitiativeInput,
  UpdateInitiativeInput,
  JoinInitiativeInput,
  SetBudgetInput,
  InitiativeEnabler,
  InviteEnablerInput,
  UpdateEnablerBudgetInput,
  UpdateEnablerStatusInput,
  EnablerInfo,
} from '@/types/initiative';
import type { JuthoorRewardsInput, CreateJuthoorInitiativePayload } from '@/types/juthoor';

/**
 * Fetch aggregate initiative statistics including participation growth and budget usage
 *
 * @returns API envelope containing InitiativeStatistics data
 */
export async function getInitiativeStatistics(): Promise<
  ApiEnvelope<InitiativeStatistics>
> {
  return apiClient.get<InitiativeStatistics>(
    '/api_partner/initiative_statistics'
  );
}

/**
 * Fetch list of initiatives with optional filters
 *
 * @param params - Optional query parameters for filtering, pagination, and search
 * @returns API envelope containing array of Initiative objects
 */
export async function getInitiatives(
  params?: InitiativeQueryParams
): Promise<ApiEnvelope<Initiative[]>> {
  const searchParams = new URLSearchParams();

  if (params?.page) {
    searchParams.set('page', String(params.page));
  }
  if (params?.per_page) {
    searchParams.set('per_page', String(params.per_page));
  }
  if (params?.category) {
    searchParams.set('category', params.category);
  }
  if (params?.status) {
    searchParams.set('status', params.status);
  }
  if (params?.type) {
    searchParams.set('type', params.type);
  }
  if (params?.search) {
    searchParams.set('search', params.search);
  }

  const queryString = searchParams.toString();
  const path = `/api_partner/initiatives${queryString ? `?${queryString}` : ''}`;

  return apiClient.get<Initiative[]>(path);
}


/**
 * Fetch city list for the wizard city dropdown
 */
export async function getCities(): Promise<ApiEnvelope<CityLookup[]>> {
  return apiClient.get<CityLookup[]>('/api_partner/cities');
}

/**
 * Fetch communities filtered by city
 */
export async function getCommunities(
  cityId?: string
): Promise<ApiEnvelope<CommunityLookup[]>> {
  const path = cityId
    ? `/api_partner/communities?city_id=${encodeURIComponent(cityId)}`
    : '/api_partner/communities';
  return apiClient.get<CommunityLookup[]>(path);
}

const CP_TO_NC = 0.01;

/**
 * Build the exact wire-format payload for POST /api_partner/initiatives.
 * Maps wizard state fields to the field names expected by StoreInitiativeRequest.
 */
export function buildCreateInitiativePayload(
  template: InitiativeTemplate,
  basicInfo: CreateInitiativeInput,
  rewards: CreateInitiativeRewardsInput,
  initBudget: number
): CreateInitiativePayload {
  return {
    type: template.slug,
    title: basicInfo.title_en,
    title_ar: basicInfo.title_ar,
    scope: basicInfo.scope,
    visibility: 'open',
    start_date: rewards.start_date,
    end_date: rewards.end_date ?? null,
    participant_max: basicInfo.participant_max_type === 'custom' ? (basicInfo.participant_max ?? null) : null,
    points_for: template.default_points_for,
    init_budget: initBudget,
    goal: rewards.goal,
    goal_time: rewards.goal_time,
    reward: rewards.reward,
    nc: rewards.nc,
    locations: (basicInfo.community_ids && basicInfo.community_ids.length > 0)
      ? basicInfo.community_ids.map((id) => ({
          city_id: basicInfo.city_id ?? '',
          community_id: id,
        }))
      : [{
          city_id: basicInfo.city_id ?? '',
        }],
    walking_area_type: rewards.walking_area_type,
    walking_area: rewards.walking_area,
  };
}

export { CP_TO_NC };

/**
 * Build the wire-format payload for POST /api_partner/juthoor/initiatives.
 * Maps wizard basic info + Juthoor rewards step data to the API contract.
 */
export function buildJuthoorInitiativePayload(
  basicInfo: CreateInitiativeInput,
  juthoorData: JuthoorRewardsInput
): CreateJuthoorInitiativePayload {
  return {
    type: 'b2c',
    title_en: basicInfo.title_en,
    title_ar: basicInfo.title_ar,
    description_en: basicInfo.description_en,
    description_ar: basicInfo.description_ar,
    total_trees: juthoorData.total_trees,
    reward_per_tree: juthoorData.reward_per_tree,
    max_trees_per_client: juthoorData.max_trees_per_client,
    netzero_land_id: juthoorData.netzero_land_id,
    land_name: juthoorData.land_name || null,
    land_polygon: juthoorData.land_polygon ?? null,
    start_date: juthoorData.start_date,
    budget: juthoorData.budget,
    locations: (basicInfo.community_ids && basicInfo.community_ids.length > 0)
      ? basicInfo.community_ids.map((id) => ({
          city_id: basicInfo.city_id,
          community_id: id,
        }))
      : [{ city_id: basicInfo.city_id }],
  };
}

/**
 * Create a new initiative
 */
export async function createInitiative(
  payload: CreateInitiativePayload
): Promise<ApiEnvelope<Initiative>> {
  return apiClient.post<Initiative>('/api_partner/initiatives', payload);
}

/**
 * Fetch a single initiative by ID
 */
export async function getInitiativeById(
  id: string
): Promise<ApiEnvelope<Initiative>> {
  return apiClient.get<Initiative>(`/api_partner/initiatives/${id}`);
}


/**
 * Fund an initiative from the partner wallet (set budget)
 */
export async function fundInitiative(
  id: string,
  input: SetBudgetInput
): Promise<ApiEnvelope<Initiative>> {
  return apiClient.post<Initiative>(`/api_partner/initiatives/${id}/set-budget`, input);
}

/**
 * Fetch enablers available to invite (excludes the current partner's own enabler)
 */
export async function getAvailableEnablers(
  search?: string
): Promise<ApiEnvelope<EnablerInfo[]>> {
  const path = search
    ? `/api_partner/enablers?search=${encodeURIComponent(search)}`
    : '/api_partner/enablers';
  return apiClient.get<EnablerInfo[]>(path);
}

/**
 * Invite an enabler to an initiative
 */
export async function inviteParticipant(
  initiativeId: string,
  input: InviteParticipantInput
): Promise<ApiEnvelope<{ message: string }>> {
  return apiClient.post<{ message: string }>(
    `/api_partner/initiatives/${initiativeId}/enablers/invite`,
    input
  );
}

/**
 * Reactivate a completed or expired initiative
 */
export async function reactivateInitiative(
  id: string,
  input: ReactivateInitiativeInput
): Promise<ApiEnvelope<Initiative>> {
  return apiClient.post<Initiative>(
    `/api_partner/initiatives/${id}/reactivate`,
    input
  );
}

/**
 * Update an existing initiative
 */
export async function updateInitiative(
  id: string,
  input: UpdateInitiativeInput
): Promise<ApiEnvelope<Initiative>> {
  return apiClient.put<Initiative>(`/api_partner/initiatives/${id}`, input);
}

/**
 * Delete an initiative
 */
export async function deleteInitiative(
  id: string
): Promise<ApiEnvelope<null>> {
  return apiClient.delete<null>(`/api_partner/initiatives/${id}`);
}

/**
 * Fetch open initiatives (public listing)
 */
export async function getOpenInitiatives(): Promise<ApiEnvelope<Initiative[]>> {
  return apiClient.get<Initiative[]>('/api_partner/open_initiatives');
}

/**
 * Fetch initiatives the partner has participated in
 */
export async function getParticipatedInitiatives(): Promise<ApiEnvelope<Initiative[]>> {
  return apiClient.get<Initiative[]>('/api_partner/initiatives/participated');
}

/**
 * Join an initiative as a participant/enabler
 */
export async function joinInitiative(
  id: string,
  input: JoinInitiativeInput
): Promise<ApiEnvelope<Initiative>> {
  return apiClient.post<Initiative>(`/api_partner/initiatives/${id}/join`, input);
}

/**
 * Fetch enablers for an initiative
 */
export async function getInitiativeEnablers(
  initiativeId: string
): Promise<ApiEnvelope<InitiativeEnabler[]>> {
  return apiClient.get<InitiativeEnabler[]>(
    `/api_partner/initiatives/${initiativeId}/enablers`
  );
}

/**
 * Update an enabler's budget for an initiative
 */
export async function updateEnablerBudget(
  initiativeId: string,
  enablerId: string,
  input: UpdateEnablerBudgetInput
): Promise<ApiEnvelope<InitiativeEnabler>> {
  return apiClient.post<InitiativeEnabler>(
    `/api_partner/initiatives/${initiativeId}/enablers/${enablerId}/budget`,
    input
  );
}

/**
 * Update an enabler's status for an initiative
 */
export async function updateEnablerStatus(
  initiativeId: string,
  enablerId: string,
  input: UpdateEnablerStatusInput
): Promise<ApiEnvelope<InitiativeEnabler>> {
  return apiClient.post<InitiativeEnabler>(
    `/api_partner/initiatives/${initiativeId}/enablers/${enablerId}/status`,
    input
  );
}

/**
 * Fetch an enabler invitation by initiative enabler ID
 */
export async function getEnablerInvitation(
  initiativeEnablerId: string
): Promise<ApiEnvelope<InitiativeEnabler>> {
  return apiClient.get<InitiativeEnabler>(
    `/api_partner/invite_enabler/${initiativeEnablerId}`
  );
}

/**
 * Fetch initiative templates from the API
 */
export async function getInitiativeTemplates(): Promise<ApiEnvelope<InitiativeTemplate[]>> {
  return apiClient.get<InitiativeTemplate[]>('/api_partner/initiatives/templates');
}

// Re-export FundInitiativeInput for backward compatibility
export type { FundInitiativeInput, InviteEnablerInput };
